import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Eye, EyeOff, TrendingUp, Trophy } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { authApi, challengeApi } from '../lib/api';
import { canCheckout, PLAN_PRICES } from '../lib/billing';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { MimiqueIcon } from '../components/MimiqueLogo';
import { C, F, CTA_PRIMARY, SiteHeader } from '../components/SiteChrome';
import FaqList from '../components/FaqList';
import PostSlides from '../components/PostSlides';
import ImageLightbox from '../components/ImageLightbox';
import { fetchPost, imageSrc } from '../lib/blogApi';
import ChallengeCountdown, { CHALLENGE_CLOSES } from '../components/ChallengeCountdown';
import { testimonials, TestimonialCarousel, TestimonialCard, QuoteStyles, SectionEyebrow, StepCard } from './Landing';

/*
 * The November 30-day challenge sales page, /challenge.
 *
 * It links nowhere else on purpose, so a reader stays here until they join.
 * Joining is: make an account (or log in), then a Stripe checkout for
 * Unlimited whose first charge is the start date. Until then they are on Free
 * and can learn the app. The backend holds all of that (/api/challenge/*,
 * activate_challenge_cohort in server.py); this page only explains and sends.
 */

// Fallback until /challenge/status answers. The server's copy wins.
// The September challenge blog post, whose slides are shown here as they are
// on the blog. Fetched, so editing the slides in /admin/blog changes both.
const SEPTEMBER_POST_SLUG = '30-day-french-speaking-challenge';


const BLOG_IMG = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/blog/images/${id}`;
const IMG = {
    hero: '/assets/challenge-hero.webp',
    founder: '/assets/founder.jpg',
    tracker: BLOG_IMG('0ce00cff-70ec-4de1-a4f0-be1289375add'),
    graph: BLOG_IMG('b04dfe58-a7e9-4b28-88c3-130a72746b4f'),
};

const painPoints = [
    "You understand far more French than you can say.",
    "Someone speaks to you and your mind goes blank.",
    "You have to repeat yourself at the bar, the bakery or on the phone, you're never understood first time.",
    "You are fed up of misunderstandings.",
    "People switch to English the second they hear your accent.",
    "You've done the classes and the apps, but speaking still feels scary.",
];

const whatYouGet = [
    { title: '30 days of Unlimited', body: 'Every Mimique feature: unlimited phrases, pronunciation scores and word-level pronunciation feedback on every recording, 20+ natural French voices and AI phrase generation.' },
    { title: 'Sound lessons', body: 'Listen and repeat lessons on the sounds English speakers struggle with most: the nasals (an, on, in), the French r, ou and u, i, é and è. When your pronunciation feedback picks up one of these sounds, Mimique offers you the lesson for it, so you can fine-tune it there and then.' },
    { title: 'Your own challenge phrases', body: "A set of phrases made just for you, from your answers to the welcome questions (your level and why you're learning) and the sounds you find hardest. They're ready once you've done a couple of practice sessions, so I can hear where to focus. They're your test: the base scores to measure your improvement." },
    { title: 'Weekly score check-ins', body: 'Practise anything you like day to day. Once a week you practise your challenge phrases, the same set every time, so you can see your scores climb each week from day 1 to day 30.' },
    { title: 'A daily tracker', body: 'A 30-day grid on your dashboard that ticks off every day you do your five minutes, so you can see your streak build. You can also have a daily reminder delivered to your inbox.' },
    { title: 'Start the day you join', body: "No waiting for a start date: join any day up to 20 November and your 30 days start that day." },
    { title: 'Me, on hand', body: "I'll be there throughout the challenge to encourage you and offer tips. Questions, problems or ideas? Email me. Challengers also get a say in how to improve the app and the challenge." },
    // The prize: last, in gold with a trophy so it stands out.
    { title: 'A free month of Unlimited', body: 'Practise for five minutes every day for all 30 days and your next month of Unlimited is on me.', prize: true },
];

// Header menu: this page's own sections only, nothing that leaves the page.
const menuLinks = [
    ['what', 'The challenge'],
    ['results', 'Results'],
    ['get', 'What you get'],
    ['how', 'How it works'],
    ['who', "Who it's for"],
    ['faq', 'FAQ'],
];

// The free-month prize, in gold with a trophy: the app's own amber, as on the
// Founding Member badge. One component so every mention of it matches.
const PrizeNote = ({ children, onDark = false, style }) => (
    <span style={{
        display: 'flex', alignItems: 'center', gap: 10, borderRadius: 14, padding: '10px 14px', fontWeight: 700,
        ...(onDark
            ? { background: 'rgba(245,158,11,0.14)', border: '2px solid #fcd34d', color: '#fcd34d' }
            : { background: '#fef3c7', border: '1px solid #fcd34d', color: '#92400e' }),
        ...style,
    }}>
        <Trophy size={22} strokeWidth={2.2} color="#f59e0b" aria-hidden="true" style={{ flexShrink: 0 }} />
        <span>{children}</span>
    </span>
);

const timeline = [
    // No dates: people join any day up to 20 November, and their day 1 is the
    // day their challenge phrases are ready.
    { when: 'When you join', what: `Grab your place: the ${PLAN_PRICES.unlimited} payment is taken and you have access to Mimique Unlimited. Answer the welcome questions, then do a couple of practice sessions on any phrases you like. Your challenge phrases are made from your answers and the sounds those recordings show you find hardest. If there's anything specific you want to add, get in touch so I can include it.` },
    { when: 'Day 1', what: 'Your challenge phrases are ready and your 30 days start. Do your first practice session on them to get your baseline score, the starting point everything is measured against.' },
    { when: 'Every day', what: "Five minutes, practising whatever you want: the phrases you need this week, a sound that keeps tripping you up. Listen, repeat, record, compare. That's it, simple!" },
    { when: 'Once a week', what: 'Practise your challenge phrases. This is your test: the same phrases every week, so your scores show exactly how much you\'ve improved.' },
    { when: 'Day 30', what: 'Your final practice and score, side by side with day 1.' },
    { when: 'Beyond day 30', what: <>
        Unleash your new-found speaking skills and speak more confidently going forward, with or without Mimique.
        <PrizeNote style={{ marginTop: 10 }}>Did all 30 days? Your next month of Unlimited is free.</PrizeNote>
    </> },
];

const challengeFaqs = [
    { q: 'What is a 30-day French speaking challenge?', a: 'A simple daily habit to improve your French speaking and pronunciation. Just five minutes a day using the shadowing technique on phrases you choose, focusing on the sounds you need to work on. It includes pronunciation feedback, tips and sound drills on the common French sounds anglophones struggle with.' },
    { q: 'When does it start?', a: "The day your challenge phrases are ready, usually after a couple of practice sessions. That's your day 1, and it runs for 30 days from then, so you never lose a day waiting for them." },
    { q: 'When do sign-ups close?', a: 'You can join any day up to and including Friday 20 November.' },
    { q: 'When am I charged?', a: `When you join: ${PLAN_PRICES.unlimited} for a month of Unlimited, and you go straight onto Unlimited.` },
    { q: 'Can I cancel?', a: "Yes, any time, in the app under Settings. It's a normal monthly subscription, and you keep Unlimited until the end of the month you've paid for." },
    { q: 'What happens after the 30 days?', a: 'Your Unlimited subscription carries on monthly unless you cancel. If you did your five minutes on all 30 days, your next month is free.' },
    { q: 'What if I miss a day?', a: "You won't get the free month, but keep going anyway. The progress is still yours, and the point is speaking better French, not a perfect streak." },
    { q: 'Is it for beginners or advanced?', a: "Yes. Beginners to advanced. If you're starting out, your phrases are everyday basics. If you already speak French, you work on the sounds and phrases that trip you up." },
    { q: 'Do I need to live in France?', a: "No. It's for anyone learning French: people living here, people planning a trip or a move, people who just love the language." },
    { q: 'Phone or computer?', a: "Either, whichever suits you. Mimique works in the web browser on your phone, tablet or computer, so there's nothing to download. On your phone you can add it to your home screen and open it just like any other app." },
    { q: 'I already pay for Mimique. Can I join?', a: "Yes! Email hello@mimique.app and I'll add you, so you're not charged twice." },
];

// ── Countdown ───────────────────────────────────────────────────────────────

// Shared with the dashboard, so the two always match.
const Countdown = ChallengeCountdown;

const Tick = ({ onDark = false }) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: 1 }} aria-hidden="true">
        <circle cx="12" cy="12" r="11" fill={onDark ? C.light : '#e3f3e8'} />
        <path d="M7 12.5l3 3 7-7" stroke={onDark ? C.deep : C.bright} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const Cta = ({ children, onClick, style }) => (
    <button className="mq-cta mq-arrowbtn" onClick={onClick} style={{ ...CTA_PRIMARY, padding: '15px 38px', ...style }}>
        {children}<span className="mq-arrow" aria-hidden="true">&rarr;</span>
    </button>
);

const Section = ({ id, bg = '#ffffff', children, width = 1160 }) => (
    <div id={id} className="mq-section" style={{ background: bg, padding: '110px 24px' }}>
        <div style={{ maxWidth: width, margin: '0 auto' }}>{children}</div>
    </div>
);

const H2 = ({ children, onDark = false, center = false }) => (
    <h2 style={{ margin: '0 0 18px', fontFamily: F.heading, fontSize: 36, fontWeight: 800, lineHeight: 1.2, color: onDark ? '#ffffff' : C.deep, textAlign: center ? 'center' : 'left' }}>
        {children}
    </h2>
);

const P = ({ children, onDark = false, style }) => (
    <p style={{ margin: '0 0 16px', fontSize: 16.5, lineHeight: 1.75, color: onDark ? C.onDarkMuted : C.bodyOnWhite2, ...style }}>{children}</p>
);

// ── Page ────────────────────────────────────────────────────────────────────

const Challenge = () => {
    const { user, login, register } = useAuth();
    const navigate = useNavigate();
    const joinRef = useRef(null);

    const [status, setStatus] = useState(null);
    const [joined, setJoined] = useState(false);
    const [justPaid] = useState(() => new URLSearchParams(window.location.search).get('joined') === '1');
    // Back from a password reset (?login=1), they already have an account.
    const [mode, setMode] = useState(() =>
        new URLSearchParams(window.location.search).get('login') ? 'login' : 'signup'); // 'signup' | 'login'
    const [resetSentTo, setResetSentTo] = useState('');
    const [form, setForm] = useState({ name: '', email: '', password: '' });
    const [showPassword, setShowPassword] = useState(false);
    const [busy, setBusy] = useState(false);
    const [slides, setSlides] = useState([]);
    const [lightbox, setLightbox] = useState(null);

    useEffect(() => {
        fetchPost(SEPTEMBER_POST_SLUG).then(p => setSlides(p?.slides || [])).catch(() => {});
    }, []);

    useEffect(() => {
        challengeApi.status().then(r => setStatus(r.data)).catch(() => {});
        if (justPaid) window.history.replaceState({}, '', window.location.pathname);
    }, [justPaid]);

    useEffect(() => {
        if (!user) { setJoined(false); return; }
        challengeApi.me().then(r => setJoined(!!r.data.joined)).catch(() => {});
    }, [user]);

    // The countdowns run to when sign-ups close (midnight, end of day 1), not the
    // start, so they show how long is left to join.
    const closesAt = status?.closes_at || CHALLENGE_CLOSES;
    const spotsLeft = status ? status.spots_left : null;
    const isOpen = status ? status.open : true;
    const youreIn = joined || justPaid;

    const scrollToJoin = () => joinRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    const goToCheckout = async () => {
        const res = await challengeApi.checkout();
        if (res.data?.url) window.location.href = res.data.url;
        else throw new Error('No checkout link');
    };

    const handleJoin = async (e) => {
        e?.preventDefault();
        setBusy(true);
        try {
            if (!user) {
                const email = form.email.trim();
                if (mode === 'signup') await register(email, form.password, form.name.trim());
                else await login(email, form.password);
            }
            await goToCheckout();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (err?.response?.status === 409 && /already have a place/i.test(detail || '')) setJoined(true);
            toast.error(detail || 'Something went wrong. Please try again.');
            setBusy(false);
        }
    };

    // Without this, someone with an old account and a forgotten password had
    // to leave the page to reset it, and many would not find their way back.
    const handleForgot = async () => {
        const email = form.email.trim();
        if (!email) { toast.error('Type your email above first.'); return; }
        try {
            // The reset page sends them back here once the new password is set.
            try { localStorage.setItem('reset_return', '/challenge?login=1'); } catch { /* private mode */ }
            await authApi.forgotPassword(email);
            setResetSentTo(email);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Something went wrong. Please try again.');
        }
    };


    return (
        <div id="top" style={{ background: '#ffffff', color: C.deep, fontFamily: F.body }}>
            <style>{`
                html { scroll-behavior: smooth; }
                #join, .mq-ch-form { scroll-margin-top: 90px; }
                .mq-ch-two { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 56px; align-items: center; }
                .mq-ch-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 18px; }
                /* "speaking" alone on a line on phones, so the break is for wider screens. */
                @media (max-width: 600px) { .mq-ch-h1br { display: none; } }
                /* Same in the narrow two-column hero, where the title shares the row with the photo. */
                @media (min-width: 861px) and (max-width: 1100px) { .mq-ch-h1br { display: none; } }
                @media (max-width: 860px) {
                    .mq-ch-two { grid-template-columns: minmax(0, 1fr); gap: 36px; }
                    .mq-ch-grid { grid-template-columns: minmax(0, 1fr); }
                }
                @media (max-width: 700px) {
                    .mq-section { padding-top: 72px !important; padding-bottom: 72px !important; }
                    h1, h2 { line-height: 1.15 !important; }
                    .mq-ch-hero h2 { font-size: 21px !important; }
                    /* Same top gap as the home page hero on a phone. */
                    .mq-ch-hero { padding-top: 40px !important; }
                }
            `}</style>

            <SiteHeader cta={youreIn ? { label: 'Open Mimique', onClick: () => navigate('/dashboard'), links: menuLinks } : { label: 'Grab my place', onClick: scrollToJoin, links: menuLinks }} />

            {/* HERO: the pain point first, then the offer. */}
            {/* overflow clip, not hidden: it still trims the logo shape, but
                hidden would stop the left column sticking on desktop. */}
            <div className="mq-ch-hero" style={{ background: `linear-gradient(180deg, ${C.primary} 0%, ${C.deep} 100%)`, padding: '80px 24px 100px', position: 'relative', overflow: 'clip' }}>
                <div className="mq-motif mq-motif--hero" aria-hidden="true">
                    <MimiqueIcon size={460} variant="bare" />
                </div>
                {/* Title in the left column, so the photo starts level with it on desktop. */}
                {/* The offer and the form, centred against each other so the two
                    columns sit level. */}
                <div className="mq-ch-two" style={{ maxWidth: 1160, margin: '0 auto', position: 'relative', zIndex: 1, alignItems: 'center' }}>
                    <div className="mq-ch-herotext" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 22 }}>
                        <div>
                            <h1 style={{ margin: 0, fontFamily: F.heading, fontSize: 'clamp(34px, 3.6vw, 42px)', lineHeight: 1.12, fontWeight: 800, letterSpacing: '-0.02em', color: '#ffffff' }}>
                                30-Day <span style={{ color: '#b8ecce' }}>French Speaking<br className="mq-ch-h1br" /> & Pronunciation</span> Challenge
                            </h1>
                            <div style={{ marginTop: 12, fontFamily: F.brand, fontSize: 18, fontWeight: 700, color: '#ffffff' }}>Practise for just 5 minutes a day</div>
                        </div>
                        {/* The offer, up top: one month of Unlimited is all the
                            challenge costs, December free for finishing, and
                            cancelling straight away is fine. */}
                        {!youreIn && (
                            <div style={{ width: '100%', maxWidth: 520, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.22)', borderRadius: 22, padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                                    <span style={{ fontFamily: F.brand, fontSize: 15, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#b8ecce' }}>Only</span>
                                    <span style={{ fontFamily: F.brand, fontSize: 40, fontWeight: 800, lineHeight: 1, color: '#ffffff' }}>{PLAN_PRICES.unlimited}</span>
                                </div>
                                <div style={{ fontSize: 15.5, lineHeight: 1.55, color: C.onDarkPrimary }}>
                                    One month of Unlimited access to Mimique, with your own challenge phrases, sound lessons and daily tracker: everything you need to complete the challenge and improve your spoken French.
                                </div>
                                <PrizeNote onDark style={{ fontSize: 15.5, lineHeight: 1.45 }}>Do all 30 days and your next month is FREE!</PrizeNote>
                            </div>
                        )}
                        {/* Joined: the "You're in" message is in the form card on
                            the right, so only the prize reminder here. */}
                        {youreIn ? (
                            <PrizeNote onDark style={{ fontSize: 15.5, lineHeight: 1.45 }}>Do all 30 days and your next month is FREE!</PrizeNote>
                        ) : (
                            <>
                                <div style={{ fontFamily: F.brand, fontSize: 20, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#ffffff', marginBottom: -14 }}>⏳ Sign-ups close in</div>
                                <div style={{ fontSize: 15, fontWeight: 700, color: '#b8ecce', marginBottom: -8 }}>Now extended until Friday 20 November! Your 30 days start when your phrases are ready.</div>
                                <Countdown target={closesAt} wide />
                            </>
                        )}
                    </div>
                    {/* The form first, then the photo, so joining is right at the
                        top. The header's Grab my place button scrolls here. */}
                    <div id="join" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 28, width: '100%' }}>
                        <div style={{ width: '100%', maxWidth: 440 }}>
                        <div ref={joinRef} className="mq-ch-form" style={{ background: '#ffffff', borderRadius: 28, padding: '30px 28px', boxShadow: '0 30px 70px -25px rgba(0,0,0,0.55)' }}>
                            {!youreIn && (
                                <div style={{ fontFamily: F.heading, fontSize: 24, fontWeight: 800, lineHeight: 1.2, color: C.deep, marginBottom: 14 }}>
                                    Join the November challenge
                                </div>
                            )}
                            {youreIn ? (
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontFamily: F.brand, fontSize: 24, fontWeight: 800, color: C.deep }}>You're in! 🎉</div>
                                    <p style={{ margin: '10px 0 20px', fontSize: 15, lineHeight: 1.6, color: C.bodyOnWhite2 }}>
                                        Your place is booked. Answer the welcome questions and do a couple of practice sessions: your challenge phrases are made from your answers and the sounds you find hardest.
                                    </p>
                                    <Cta onClick={() => navigate('/dashboard')}>Open Mimique</Cta>
                                </div>
                            ) : !isOpen ? (
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontFamily: F.brand, fontSize: 22, fontWeight: 800, color: C.deep }}>
                                        {spotsLeft === 0 ? 'All 30 places are taken' : 'Sign-ups for November have closed'}
                                    </div>
                                    <p style={{ margin: '10px 0 0', fontSize: 15, lineHeight: 1.6, color: C.bodyOnWhite2 }}>
                                        Thank you for the interest! Email hello@mimique.app and I'll let you know when the next one opens.
                                    </p>
                                </div>
                            ) : !canCheckout() ? (
                                <p style={{ margin: 0, fontSize: 15, lineHeight: 1.6, color: C.bodyOnWhite2 }}>
                                    To join, open mimique.app/challenge in your phone's browser rather than the app.
                                </p>
                            ) : (
                                <form onSubmit={handleJoin} className="space-y-4">
                                    <div style={{ fontSize: 14.5, lineHeight: 1.55, color: C.bodyOnWhite2 }}>
                                        {user
                                            ? `Hi ${user.name?.split(' ')[0] || 'there'}! You're logged in as ${user.email}. Next is the secure payment page.`
                                            : mode === 'signup'
                                                ? 'Create your account, then you go to the secure payment page.'
                                                : 'Log in to your account, then you go to the secure payment page.'}
                                    </div>
                                    {!user && (
                                        <>
                                            {mode === 'signup' && (
                                                <div className="space-y-2">
                                                    <Label htmlFor="ch-name">Your name</Label>
                                                    <Input id="ch-name" autoComplete="name" required value={form.name}
                                                        onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-secondary" />
                                                </div>
                                            )}
                                            <div className="space-y-2">
                                                <Label htmlFor="ch-email">Email</Label>
                                                <Input id="ch-email" type="email" inputMode="email" autoComplete={mode === 'signup' ? 'email' : 'username'}
                                                    autoCapitalize="none" autoCorrect="off" spellCheck={false} required placeholder="your@email.com"
                                                    value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-secondary" />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="ch-password">Password</Label>
                                                <div className="relative">
                                                    <Input id="ch-password" type={showPassword ? 'text' : 'password'}
                                                        autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                                                        autoCapitalize="none" autoCorrect="off" spellCheck={false} required minLength={mode === 'signup' ? 8 : undefined}
                                                        value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-secondary pr-10" />
                                                    <button type="button" onClick={() => setShowPassword(v => !v)} aria-label={showPassword ? 'Hide password' : 'Show password'}
                                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1">
                                                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                                    </button>
                                                </div>
                                                {mode === 'login' && (resetSentTo ? (
                                                    <p className="text-sm text-muted-foreground">
                                                        If <strong>{resetSentTo}</strong> has an account, a reset link is on its
                                                        way. Check your spam folder if you don't see it. Once you've set a new
                                                        password, you come straight back here to grab your place.
                                                    </p>
                                                ) : (
                                                    <button type="button" onClick={handleForgot}
                                                        className="text-sm text-muted-foreground hover:text-foreground underline">
                                                        Forgot your password?
                                                    </button>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                    <Button type="submit" className="w-full rounded-full bg-primary hover:bg-primary/90 h-12 text-base font-bold" disabled={busy}>
                                        {busy ? 'One moment...' : 'Grab my place'}
                                    </Button>
                                    {!user && (
                                        <button type="button" className="w-full text-sm text-muted-foreground hover:text-foreground text-center"
                                            onClick={() => setMode(mode === 'signup' ? 'login' : 'signup')}>
                                            {mode === 'signup' ? 'Already have a Mimique account? Log in' : 'New to Mimique? Create an account'}
                                        </button>
                                    )}
                                    <div style={{ fontSize: 12.5, lineHeight: 1.5, color: C.bodyOnWhite, textAlign: 'center' }}>
                                        {PLAN_PRICES.unlimited}/month, cancel any time in Settings.
                                    </div>
                                </form>
                            )}
                        </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* PAIN */}
            <Section width={1000}>
                {/* The words on the left, the photo beside them, level with the
                    title. */}
                <div className="mq-ch-two" style={{ alignItems: 'start' }}>
                    <div>
                        <SectionEyebrow>Sound familiar?</SectionEyebrow>
                        <h2 style={{ margin: '0 0 16px', fontFamily: F.heading, fontSize: 30, lineHeight: 1.2, fontWeight: 800, color: C.deep }}>
                            Why is speaking French so difficult?
                        </h2>
                        <div style={{ fontSize: 17, fontWeight: 700, lineHeight: 1.6, color: C.deep, marginBottom: 18 }}>
                            You know the words. So why won't they come out? And when they do, why does nobody understand you, or reply in English?
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, margin: '0 0 28px' }}>
                            {painPoints.map((p) => (
                                <div key={p} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', fontSize: 17, lineHeight: 1.6, color: C.deep }}>
                                    <span aria-hidden="true" style={{ color: '#d97757', fontWeight: 800, fontSize: 18, lineHeight: 1.5 }}>✕</span>
                                    <span>{p}</span>
                                </div>
                            ))}
                        </div>
                        <div style={{ fontFamily: F.heading, fontSize: 22, fontWeight: 700, lineHeight: 1.3, color: C.deep, marginBottom: 12 }}>
                            Understanding French and speaking it are two different skills.
                        </div>
                        <P>
                            You need to practise saying words and phrases out loud, repeating them until the pronunciation feels more natural and you can say them with confidence. That's the kind of practice most apps and courses leave out.
                        </P>
                        <P style={{ marginBottom: 0 }}>
                            Five minutes a day for 30 days to practise speaking French out loud, improve your pronunciation with feedback on every phrase, word and sound, and build the confidence to speak French without freezing. Practise with phrases you actually need in everyday life.
                        </P>
                    </div>
                    {/* The September challenge's slides from the blog post, one
                        at a time. The photo stands in until they load, or if the
                        post has none. */}
                    {slides.length > 0 ? (
                        <div style={{ width: '100%', maxWidth: 420, justifySelf: 'center' }}>
                            <PostSlides
                                single
                                style={{ margin: 0 }}
                                slides={slides}
                                onOpen={(i) => setLightbox({ images: slides.map(x => ({ src: imageSrc(x.src), alt: x.alt })), index: i })}
                            />
                        </div>
                    ) : (
                        <img
                            src={IMG.hero}
                            alt="Leanne practising a French phrase in Mimique, with her pronunciation score on screen"
                            style={{ width: '100%', maxWidth: 380, justifySelf: 'center', aspectRatio: '4 / 5', objectFit: 'cover', borderRadius: 32, boxShadow: '0 30px 70px -25px rgba(0,0,0,0.35)' }}
                        />
                    )}
                </div>
            </Section>

            {/* WHAT IT IS */}
            <Section id="what" bg={C.paleBg}>
                <div className="mq-ch-two">
                    <div>
                        <SectionEyebrow>The challenge</SectionEyebrow>
                        <H2>A daily speaking habit, five minutes a day</H2>
                        <P>
                            A 30-day French speaking challenge is a daily habit of short practice sessions focused on speaking. No textbook phrases, vocab or grammar overload. Just you, Mimique and five minutes a day.
                        </P>
                        <P>
                            Unlike other apps, programmes and classes, you're not following someone else's lesson plan. You work on what <em>you</em> want to say and the sounds <em>you</em> find hard.
                        </P>
                        <P style={{ marginBottom: 0 }}>
                            You'll tune your ear to how French really sounds, copy a native speaker's rhythm and melody (that's shadowing), and do drills on the French sounds English speakers struggle with most.
                        </P>
                    </div>
                    <img src={IMG.tracker} alt="The 30-day challenge tracker in Mimique, day 30 of 30" style={{ width: '100%', borderRadius: 24, boxShadow: '0 20px 50px -30px rgba(10,22,16,0.45)' }} />
                </div>
                <div className="mq-stepgrid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 20, marginTop: 56 }}>
                    <StepCard
                        n={1}
                        title="Listen"
                        body="Hear a native French voice say your phrase. Slow it down if you need to."
                        iconPath={<>
                            <path d="M4 13v-1a8 8 0 0 1 16 0v1" stroke="#e9f4ec" strokeWidth="2" strokeLinecap="round" />
                            <rect x="2.5" y="13" width="5" height="7" rx="2" fill="#e9f4ec" />
                            <rect x="16.5" y="13" width="5" height="7" rx="2" fill="#e9f4ec" />
                        </>}
                    />
                    <StepCard
                        n={2}
                        title="Mimic & record"
                        body="Repeat it out loud straight away while Mimique records you. Tap to record, or go hands-free."
                        iconPath={<>
                            <rect x="9" y="2" width="6" height="12" rx="3" fill="#e9f4ec" />
                            <path d="M5 11a7 7 0 0 0 14 0" stroke="#e9f4ec" strokeWidth="2" strokeLinecap="round" />
                            <path d="M12 18v4M9 22h6" stroke="#e9f4ec" strokeWidth="2" strokeLinecap="round" />
                        </>}
                    />
                    <StepCard
                        n={3}
                        title="Compare & improve"
                        body="Hear yourself next to the native voice, get a score and pronunciation feedback, with a lesson on any tricky sounds."
                        iconPath={<>
                            <rect x="3" y="14" width="4" height="7" rx="1" fill="#e9f4ec" />
                            <rect x="10" y="9" width="4" height="12" rx="1" fill="#e9f4ec" />
                            <rect x="17" y="4" width="4" height="17" rx="1" fill="#e9f4ec" />
                        </>}
                    />
                </div>
            </Section>

            {/* WHY NOW / RESULTS */}
            <Section id="results">
                <div className="mq-ch-two">
                    <div>
                        <SectionEyebrow>Tried and tested</SectionEyebrow>
                        <H2>I tested it on myself and a couple of founding members first</H2>
                        <P>
                            In September I ran this challenge as a test, for me and some of Mimique's founding members. Five minutes a day, each of us on our own problem sounds. Mine were <em>beaucoup</em>, <em>Melun</em>, the French "sion" and the French r.
                        </P>
                        <P>
                            Each week my scores crept up, and by the end I could finally say <em>beaucoup</em> and <em>Melun</em> properly. I can also order a Perrier, an Aperol Spritz and <em>vin rouge</em> without having to repeat myself!
                        </P>
                        <P>
                            It worked, and not just for me. So now I'm opening it up to everyone.
                        </P>
                        {/* Her own words, as she sent them, in the home page's quote card. */}
                        <div style={{ marginTop: 24 }}>
                            <TestimonialCard t={{
                                quote: 'Definitely sign me up! I love the challenges and the new changes to the app. Looking forward to getting back into it.',
                                name: 'Tracy P.',
                                label: 'Founding Member and did the September challenge',
                                highlight: ['I love the challenges'],
                            }} />
                            <QuoteStyles />
                        </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                        <img src={IMG.graph} alt="Average weekly score on the challenge phrases, rising from 74.9 in week 1 to 83.1 at the final test" style={{ width: '100%', borderRadius: 24, boxShadow: '0 20px 50px -30px rgba(10,22,16,0.45)' }} />
                        <div style={{ fontSize: 13, lineHeight: 1.5, color: C.bodyOnWhite }}>
                            First attempt at each phrase in each person's own challenge set, tested once a week. Average of everyone who did all five weekly tests.
                        </div>
                        {/* Under the chart. The group's figure is the chart's own
                            74.9 to 83.1 as a rise: 8.2 / 74.9 = 10.9%. */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 14 }}>
                            {[
                                ['+11%', 'my score, day 1 to day 30'],
                                ['+10.9%', 'the group\'s average score, week 1 to day 30'],
                            ].map(([big, small]) => (
                                // Gold for the increase: the app's own amber, as on
                                // the Founding Member badge and a 100% score.
                                <div key={small} style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 20, padding: '18px 18px 16px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: F.brand, fontSize: 26, fontWeight: 800, color: '#92400e', whiteSpace: 'nowrap' }}>
                                        <TrendingUp size={24} strokeWidth={2.6} color="#f59e0b" aria-hidden="true" style={{ flexShrink: 0 }} />
                                        {big}
                                    </div>
                                    <div style={{ marginTop: 4, fontSize: 13.5, lineHeight: 1.45, color: '#92400e' }}>{small}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </Section>

            {/* WHAT YOU GET */}
            <div id="get" className="mq-section" style={{ background: `linear-gradient(180deg, ${C.deep} 0%, ${C.darkest} 100%)`, padding: '110px 24px' }}>
                <div style={{ maxWidth: 1160, margin: '0 auto' }}>
                    <SectionEyebrow onDark>What you get</SectionEyebrow>
                    <H2 onDark>Everything you need to improve your spoken French</H2>
                    <P onDark style={{ marginBottom: 40 }}>For the whole 30 days (and as long as you stay with Mimique after the challenge).</P>
                    <div className="mq-ch-grid">
                        {whatYouGet.map((item) => (
                            <div key={item.title} style={item.prize
                                // Gold border and a trophy. The app's own amber,
                                // as on the Founding Member badge.
                                ? { background: 'rgba(245,158,11,0.14)', border: '2px solid #fcd34d', borderRadius: 24, padding: '24px 26px', display: 'flex', gap: 14 }
                                : { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 24, padding: '24px 26px', display: 'flex', gap: 14 }}>
                                {item.prize
                                    ? <Trophy size={34} strokeWidth={2.2} color="#f59e0b" aria-hidden="true" style={{ flexShrink: 0 }} />
                                    : <Tick onDark />}
                                <div>
                                    <div style={{ fontFamily: F.brand, fontSize: item.prize ? 20 : 17, fontWeight: item.prize ? 800 : 700, color: item.prize ? '#fcd34d' : '#ffffff' }}>{item.title}</div>
                                    <div style={{ marginTop: 6, fontSize: 14.5, lineHeight: 1.6, color: 'rgba(243,249,245,0.75)' }}>{item.body}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* HOW IT WORKS: dates and money */}
            <Section id="how" width={820}>
                <SectionEyebrow>How it works</SectionEyebrow>
                <H2>Simple steps and timeline for the 30-Day Challenge</H2>
                <div style={{ display: 'flex', flexDirection: 'column', marginTop: 18 }}>
                    {timeline.map((t, i) => (
                        <div key={t.when} style={{ display: 'flex', gap: 18 }}>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <div style={{ width: 14, height: 14, borderRadius: 999, background: C.bright, marginTop: 6, flexShrink: 0 }} />
                                {i < timeline.length - 1 && <div style={{ width: 2, flex: 1, background: C.borderLight }} />}
                            </div>
                            <div style={{ paddingBottom: 26 }}>
                                <div style={{ fontFamily: F.brand, fontSize: 17, fontWeight: 800, color: C.deep }}>{t.when}</div>
                                <div style={{ marginTop: 4, fontSize: 15.5, lineHeight: 1.65, color: C.bodyOnWhite2 }}>{t.what}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </Section>

            {/* WHO IT'S FOR */}
            <Section id="who" bg={C.paleBg} width={820}>
                <SectionEyebrow>Who it's for</SectionEyebrow>
                <H2>Beginners, improvers, and everyone stuck in between</H2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 10 }}>
                    {[
                        'You live in France (or are moving there) and need French for real life: the doctor, the bank, the neighbours.',
                        "You're planning a trip and want to be understood at the restaurant or bar.",
                        'You freeze when you speak, even though you know more than you let on.',
                        'You learn with classes or another app and want the speaking practice they leave out.',
                        'You already speak decent French but want to sound more native or fluent.',
                    ].map((t) => (
                        <div key={t} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', fontSize: 16.5, lineHeight: 1.6, color: C.deep }}>
                            <Tick /><span>{t}</span>
                        </div>
                    ))}
                    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', fontSize: 17.5, lineHeight: 1.6, color: C.deep, background: '#ffffff', border: `1px solid ${C.borderLight}`, borderRadius: 20, padding: '18px 20px', marginTop: 6 }}>
                        <Tick /><span><strong>Just starting out? Even better.</strong> Start speaking French properly from the beginning, instead of spending years on grammar and still not being understood. 💚</span>
                    </div>
                </div>
            </Section>

            {/* TESTIMONIALS */}
            <Section id="testimonials">
                <div style={{ maxWidth: 640, margin: '0 auto 48px', textAlign: 'center' }}>
                    <SectionEyebrow>What people say</SectionEyebrow>
                    <H2 center>From people already using Mimique</H2>
                </div>
                <TestimonialCarousel items={testimonials} />
            </Section>

            {/* ABOUT */}
            <Section bg={C.paleBg}>
                <div className="mq-ch-two">
                    <img src={IMG.founder} alt="Leanne O'Leary, Mimique founder, holding up the Mimique app" style={{ width: '100%', maxWidth: 420, justifySelf: 'center', aspectRatio: '4 / 5', objectFit: 'cover', objectPosition: 'center', borderRadius: 32 }} />
                    <div>
                        <SectionEyebrow>Who's running it</SectionEyebrow>
                        <H2>Hi, I'm Leanne</H2>
                        <P>
                            I'm English, from Lancashire, and I've lived in France for a few years. On paper my French is good: I have the DELF B2. In real life, put me in front of a French person and I panic, stumble and forget everything. I was fed up of the endless miscommunications, and of thinking my French wasn't that bad and wondering why no one understood me!
                        </P>
                        <P style={{ marginBottom: 0 }}>
                            I'm a web developer, so I built Mimique for myself: a way to practise saying the phrases I actually need, out loud, until they come out without thinking. I then decided to open it up to other learners who have the same struggles as me. If you struggle with spoken French, I'd love you to take part and make use of Mimique with me! 💚
                        </P>
                    </div>
                </div>
            </Section>

            {/* FAQ */}
            <Section id="faq" width={800}>
                <div style={{ textAlign: 'center', marginBottom: 44 }}>
                    <SectionEyebrow>FAQ</SectionEyebrow>
                    <H2 center>Questions people ask</H2>
                </div>
                <FaqList faqs={challengeFaqs} />
            </Section>

            {lightbox && (
                <ImageLightbox
                    images={lightbox.images}
                    index={lightbox.index}
                    onIndex={(i) => setLightbox(l => ({ ...l, index: i }))}
                    onClose={() => setLightbox(null)}
                />
            )}

            {/* FOOTER. Only the legal pages, which have to be reachable from a
                page that takes payment; they open in a new tab so this one
                stays put. */}
            <div style={{ background: C.darkest, padding: '40px 24px', textAlign: 'center', fontSize: 13, color: C.onDarkMuted }}>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <a
                        href="#top"
                        className="mq-cta"
                        aria-label="Back to top"
                        onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                        style={{ cursor: 'pointer', lineHeight: 0 }}
                    >
                        <MimiqueIcon size={44} variant="square" />
                    </a>
                </div>
                <div style={{ marginTop: 14, display: 'flex', gap: 18, justifyContent: 'center', flexWrap: 'wrap' }}>
                    <a href="/legal/terms" target="_blank" rel="noopener noreferrer" style={{ color: C.footerLink }}>Terms</a>
                    <a href="/legal/privacy" target="_blank" rel="noopener noreferrer" style={{ color: C.footerLink }}>Privacy</a>
                    <span>hello@mimique.app</span>
                </div>
            </div>
        </div>
    );
};

export default Challenge;
